import { renderMetaTags, StructuredTextGraphQlResponse, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import Image from "next/image";
import { Api } from "@/api";
import {
  BottomPromoSaleBanner,
  Container,
  CustomerCareSection,
  HeroSection,
  HeroSectionSale,
  Layout,
  MidPromoSaleBanner,
  PublishKeyFeaturesSection,
  PublishVideosSection,
  PublishWorkflowSection,
  Section,
  SEO,
  SignUpForm,
  StartFreeButtons,
  CredibilityBar,
} from "@/components";
import DealOfLifeTimeBanner, {
  DealOfLifeTimeBannerType,
  dealOfLifetimeFragment,
} from "@/components/lifetime-deal/DealOfLifeTimeBanner";
import { DATOCMS_API_TOKEN, IS_GOOGLE_AUTH_ENABLED } from "@/config";
import Flothemes from "@/images/flothemes.png";
import Fstoppers from "@/images/fstoppers.png";
import PhotobizXposed from "@/images/photobiz-xposed.png";
import ShotKit from "@/images/shotkit.png";
import SlrLounge from "@/images/slr-lounge.png";
import TribeArchipelago from "@/images/tribe-archipelago.png";
import {
  carouselFragment,
  metaTagsFragment,
  CarouselFragmentType,
  responsiveImageFragment,
  Carousel,
  Stat,
  statFragment,
  StatFragmentType,
} from "@/modules";
import { SiteBanner } from "@/modules/site-banner";
import { Nullable, Subscription } from "@/types";
import { isSaleActive } from "@/utils";
import { PromoBanner, ThreeUp } from "@narrative-software/narrative-web-ui";

const COMPANY_LOGOS = [
  { src: Fstoppers, width: 155, height: 42, companyName: "Fstoppers" },
  { src: Flothemes, width: 154, height: 36, companyName: "Flothemes" },
  { src: ShotKit, width: 140, height: 40, companyName: "ShotKit" },
  { src: PhotobizXposed, width: 197, height: 44, companyName: "Photobiz Xposed" },
  { src: TribeArchipelago, width: 121, height: 121, companyName: "Tribe Archipelago", className: "lg:-my-8" },
  { src: SlrLounge, width: 136, height: 53, companyName: "SLR Lounge" },
];

const THREE_UP_DATA = [
  {
    title: "Integrate with your website",
    description:
      "Use our custom integrations with Wordpress, WIX and Squarespace. With someone else? We still work with any website.",
    image: <Image src="/images/integration-logos.png" alt="" width="225" height="38" />,
    imageHeight: 38,
  },
  {
    title: "Easy to use, easy on the eye",
    description:
      "Publish is easy to use and easy on the eye. It’s also quick to learn with our intuitive controls and in-built guidance.",
    image: <Image src="/images/heart-eyes.svg" alt="" width="32" height="31" unoptimized />,
    imageHeight: 31,
  },
  {
    title: "Super-fast loading",
    description: "Publish compresses and resizes your images so your content loads fast and is responsive for mobile.",
    image: <Image src="/images/lightning-bolt.svg" alt="" width="45" height="45" unoptimized />,
    imageHeight: 45,
  },
];

interface IPublishPage {
  page: {
    testimonials: Nullable<CarouselFragmentType>;
    stats: Nullable<StatFragmentType>;
    dealBanner: DealOfLifeTimeBannerType;
    seo: TitleMetaLinkTag[];
  };
  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
}

type PublishPageProps = {
  preview: boolean;
  subscription: Subscription<IPublishPage>;
};

const PublishPage = ({ subscription, preview }: PublishPageProps) => {
  const { data: { page: data, banner } = {} } = useQuerySubscription(subscription);

  if (!data) return null;

  const { testimonials, seo, stats, dealBanner } = data;

  return (
    <Layout product="publish" preview={preview} siteBanner={banner ? <SiteBanner {...banner} /> : null}>
      <SEO>{renderMetaTags(seo)}</SEO>
      {isSaleActive() ? (
        <HeroSectionSale
          product="publish"
          title={
            <>
              Tell your photography stories, <u>up to 50% OFF</u>
            </>
          }
          videoSrc="/videos/publish-video.webm"
          videoPoster="/images/publish-video-placeholder-hero.jpg"
          videoAspectRatio={720 / 416}
          companyLogos={COMPANY_LOGOS}
          companyLogosLabel="As seen in:"
        >
          <>
            Publish enables photographers to create beautiful, SEO optimised blog posts in minutes –{" "}
            <b>and it’s now up to 50% off for Black Friday!</b>
          </>
        </HeroSectionSale>
      ) : (
        <HeroSection
          product="publish"
          title={
            <>
              <u>Effortlessly</u> tell your photography stories.
            </>
          }
          videoSrc="/videos/publish-video.webm"
          videoPoster="/images/publish-video-placeholder-hero.jpg"
          videoAspectRatio={720 / 416}
          videoControls
        >
          Publish enables photographers to create beautiful, SEO optimised blog posts in minutes.
        </HeroSection>
      )}

      {stats ? <Stat {...stats} /> : null}

      {dealBanner ? <DealOfLifeTimeBanner {...dealBanner} /> : null}

      {testimonials && <Carousel {...testimonials} />}

      {isSaleActive() ? (
        <MidPromoSaleBanner />
      ) : (
        <PromoBanner
          title="Join thousands of photographers blogging with Publish"
          description="Get started with Publish for free on macOS."
          backgroundImagePath="/images/textures/pink-bg-alt@2x.png"
          form={
            IS_GOOGLE_AUTH_ENABLED ? (
              <StartFreeButtons product="publish" className="max-w-130" />
            ) : (
              <SignUpForm product="publish" className="max-w-160" textClassName="text-white" />
            )
          }
        />
      )}

      <div className="flex-col items-center py-12 px-12">
        <Container>
          <CredibilityBar logos={COMPANY_LOGOS} label="As seen in:" />
        </Container>
      </div>

      <PublishVideosSection />

      <PublishKeyFeaturesSection py-4812 />

      <Section className="flex-col self-center items-center w-full max-w-7xl -mt-10 lg:-mt-18 xl:-mt-36" addXPadding>
        <ThreeUp>
          {THREE_UP_DATA.map(({ title, description, ...rest }) => (
            <ThreeUp.Item key={title} title={title} {...rest}>
              {description}
            </ThreeUp.Item>
          ))}
        </ThreeUp>
      </Section>

      <PublishWorkflowSection />

      {isSaleActive() ? (
        <BottomPromoSaleBanner />
      ) : (
        <PromoBanner
          title="Build beautiful blogs with Publish"
          description="Get started with Publish for free on macOS."
          form={
            IS_GOOGLE_AUTH_ENABLED ? (
              <StartFreeButtons product="publish" className="max-w-130" />
            ) : (
              <SignUpForm product="publish" className="max-w-160" textClassName="text-white" />
            )
          }
          backgroundImagePath="/images/textures/pink-bg@2x.png"
          imageAlignment="left"
          large
        />
      )}

      <CustomerCareSection product="publish" />
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<PublishPageProps> = async ({ preview = false }) => {
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pagePublish {
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          stats {
            ...statFragment
          }
          dealBanner {
          ...lifetimeDealFragment
          }
          testimonials {
            ...testimonialCarouselFragment
          }
        }
        banner: moduleSiteBanner {
          enabled
          content {
            value
          }
        }
      }
      ${carouselFragment}
      ${statFragment}
      ${metaTagsFragment}
      ${responsiveImageFragment}
      ${dealOfLifetimeFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IPublishPage>(graphqlRequest);

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default PublishPage;
